/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { useEuiMemoizedStyles, getSecureRelForTarget, cloneElementWithCss } from '../../services';
import { validateHref } from '../../services/security/href_validator';
import { keysOf } from '../common';
import { EuiIcon } from '../icon';
import { EuiToolTip } from '../tool_tip';
import { euiContextMenuItemStyles } from './context_menu_item.styles';
export var SIZES = ['s', 'm'];
var layoutAlignToClassNames = {
  center: null,
  top: 'euiContextMenu__itemLayout--top',
  bottom: 'euiContextMenu__itemLayout--bottom'
};
export var LAYOUT_ALIGN = keysOf(layoutAlignToClassNames);
export var EuiContextMenuItem = ({
  children,
  className,
  hasPanel,
  icon,
  buttonRef,
  disabled: _disabled,
  layoutAlign = 'center',
  toolTipContent,
  toolTipProps,
  href,
  target,
  rel,
  size = 'm',
  ...rest
}) => {
  const isHrefValid = !href || validateHref(href);
  const disabled = _disabled || !isHrefValid;
  const classes = classNames('euiContextMenuItem', className);
  const styles = useEuiMemoizedStyles(euiContextMenuItemStyles);
  const cssStyles = [styles.euiContextMenuItem, styles.sizes[size], styles.layoutAlign[layoutAlign], disabled && styles.disabled];
  const iconInstance = icon && (typeof icon === 'string' ? <EuiIcon type={icon} size="m" className="euiContextMenu__icon" css={styles.euiContextMenu__icon} color="inherit" // forces the icon to inherit its parent color
  /> :
  // Assume it's already an instance of an icon.
  cloneElementWithCss(icon, {
    css: styles.euiContextMenu__icon
  }));
  const arrow = hasPanel && <EuiIcon type="arrowRight" size="m" className="euiContextMenu__arrow" css={styles.euiContextMenuItem__arrow} />;
  const textStyles = [styles.text.euiContextMenuItem__text, size === 's' && styles.text.s];
  const buttonContent = <>
      {iconInstance}
      <span className="euiContextMenuItem__text" css={textStyles}>
        {children}
      </span>
      {arrow}
    </>;
  let button;
  // <a> elements don't respect the `disabled` attribute. So if we're disabled, we'll just pretend
  // this is a button and piggyback off its disabled styles.
  if (href && !disabled) {
    const secureRel = getSecureRelForTarget({
      href,
      target,
      rel
    });
    button = <a css={cssStyles} className={classes} href={href} target={target} rel={secureRel} ref={buttonRef} {...rest}>
        {buttonContent}
      </a>;
  } else if (href || rest.onClick || toolTipContent) {
    button = <button disabled={disabled} css={cssStyles} className={classes} type="button" ref={buttonRef} {...rest}>
        {buttonContent}
      </button>;
  } else {
    button = <div css={cssStyles} className={classes} ref={buttonRef} {...rest}>
        {buttonContent}
      </div>;
  }
  if (toolTipContent) {
    const anchorClasses = classNames('eui-displayBlock', toolTipProps?.anchorClassName);
    return <EuiToolTip position="right" {...toolTipProps} anchorClassName={anchorClasses} content={toolTipContent}>
        {button}
      </EuiToolTip>;
  } else {
    return button;
  }
};
EuiContextMenuItem.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  icon: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.string.isRequired, PropTypes.any.isRequired]),
  hasPanel: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  buttonRef: PropTypes.any,
  /**
     * Required if using a tooltip. Add an optional tooltip on hover
     */
  toolTipContent: PropTypes.node,
  /**
     * Optional configuration to pass to the underlying [EuiToolTip](/#/display/tooltip).
     * Accepts any prop that EuiToolTip does, except for `content` and `children`.
     */
  toolTipProps: PropTypes.any,
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  /**
     * How to align icon with content of button
     */
  layoutAlign: PropTypes.oneOf(["center", "top", "bottom"]),
  /**
     * Reduce the size to `s` when in need of a more compressed menu
     */
  size: PropTypes.any
};
try {
  EuiContextMenuItem.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/context_menu/context_menu_item.tsx',
    description: '',
    displayName: 'EuiContextMenuItem',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      icon: {
        defaultValue: null,
        description: '',
        name: 'icon',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'EuiContextMenuItemIcon'
        }
      },
      hasPanel: {
        defaultValue: null,
        description: '',
        name: 'hasPanel',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onClick: {
        defaultValue: null,
        description: '',
        name: 'onClick',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: '(event: React.MouseEvent) => void'
        }
      },
      buttonRef: {
        defaultValue: null,
        description: '',
        name: 'buttonRef',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLButtonElement>'
        }
      },
      toolTipContent: {
        defaultValue: null,
        description: 'Required if using a tooltip. Add an optional tooltip on hover',
        name: 'toolTipContent',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      toolTipProps: {
        defaultValue: null,
        description: 'Optional configuration to pass to the underlying [EuiToolTip](/#/display/tooltip).\n' + 'Accepts any prop that EuiToolTip does, except for `content` and `children`.',
        name: 'toolTipProps',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiToolTipProps, "content" | "children">>'
        }
      },
      href: {
        defaultValue: null,
        description: '',
        name: 'href',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      target: {
        defaultValue: null,
        description: '',
        name: 'target',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      layoutAlign: {
        defaultValue: {
          value: 'center'
        },
        description: 'How to align icon with content of button',
        name: 'layoutAlign',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiContextMenuItemLayoutAlignment',
          value: [{
            value: '"center"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      size: {
        defaultValue: {
          value: 'm'
        },
        description: 'Reduce the size to `s` when in need of a more compressed menu',
        name: 'size',
        parent: {
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/context_menu/context_menu_item.tsx',
          name: 'EuiContextMenuItemProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }]
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'ButtonHTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiContextMenuItemProps']
  };
} catch (e) {}